import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import { Moment } from 'moment';
import { debounce } from 'vue-debounce';
import i18n from '@/i18n';
import { getDatepickerLocale } from '@/helpers/LocaleHelper';
import {
    getDeliveryInternalOrderColumns,
    getDeliveryOrderSelectorColumns,
} from '@/modules/Logistics/helpers/LogisticsTableHelper';
import { createDeliveryOrderTableRows } from '@/modules/Logistics/helpers/DeliveryOrderHelper';
import { onProjectRowEvent } from '@/modules/Logistics/helpers/DeliveryOrderNavigationHelper';
import type {
    DeliveryClientOption,
    DeliveryOrder,
    DeliveryRegionOption,
} from '@/modules/Logistics/interfaces/DeliveryOrderInterfaces';


export default /*#__PURE__*/_defineComponent({
  __name: 'OrderSelector',
  props: {
    orders: null,
    selectedOrderIds: null,
    selectedDates: null,
    clients: null,
    selectedClientId: null,
    clientSearchLoading: { type: Boolean },
    deliveryRegions: null,
    selectedDeliveryRegionId: null,
    loading: { type: Boolean },
    orderStatuses: null
  },
  emits: ["dates-change", "client-search", "client-change", "delivery-region-change", "selection-change"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'dates-change', dates: Moment[]): void;
    (e: 'client-search', searchQuery: string): void;
    (e: 'client-change', clientId: string | null): void;
    (e: 'delivery-region-change', deliveryRegionId: string | null): void;
    (e: 'selection-change', orderIds: string[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    orders: DeliveryOrder[];
    selectedOrderIds: string[];
    selectedDates: Moment[];
    clients: DeliveryClientOption[];
    selectedClientId?: string;
    clientSearchLoading: boolean;
    deliveryRegions: DeliveryRegionOption[];
    selectedDeliveryRegionId?: string;
    loading: boolean;
    orderStatuses: { id: string; name: string; code: string }[];
};





const locale = computed(() => getDatepickerLocale());

const expandedOrderIds = ref<string[]>(props.orders.map((order) => order.id));

const tableOrders = computed(() => createDeliveryOrderTableRows(props.orders, props.orderStatuses));

const columns = computed(() => getDeliveryOrderSelectorColumns());

const rowSelection = computed(() => ({
    selectedRowKeys: props.selectedOrderIds,
    onChange: (keys: Array<string | number>) => emit('selection-change', keys.map(String)),
}));

const emptyText = computed(() =>
    props.selectedDates.length
        ? String(i18n.t('No orders found for the selected date.'))
        : String(i18n.t('Choose a date to load orders.')),
);

const debouncedClientSearch = debounce((searchQuery: string) => {
    emit('client-search', searchQuery);
}, 300);

function handleDateAdd(date: Moment | null) {
    if (!date || props.selectedDates.some((selectedDate) => selectedDate.isSame(date, 'day'))) {
        return;
    }

    emit('dates-change', [...props.selectedDates, date.clone().startOf('day')]);
}

function handleDateRemove(date: Moment) {
    emit(
        'dates-change',
        props.selectedDates.filter((selectedDate) => !selectedDate.isSame(date, 'day')),
    );
}

function handleClientSearch(searchQuery: string) {
    debouncedClientSearch(searchQuery);
}

function handleClientChange(clientId?: string) {
    emit('client-change', clientId || null);
}

function handleDeliveryRegionChange(deliveryRegionId?: string) {
    emit('delivery-region-change', deliveryRegionId || null);
}

function getInternalOrderColumns(order: DeliveryOrder) {
    return getDeliveryInternalOrderColumns(order);
}

function toggleExpand(order: DeliveryOrder) {
    const index = expandedOrderIds.value.indexOf(order.id);
    if (index >= 0) {
        expandedOrderIds.value.splice(index, 1);
    } else {
        expandedOrderIds.value.push(order.id);
    }
}

function getCompletionRowClass(rowType: 'order' | 'internal') {
    return (order: { isCompleted: boolean }): string => {
        const completionClass = order.isCompleted ? 'delivery-order-row--completed' : 'delivery-order-row--incomplete';

        return rowType === 'order' ? `${completionClass} delivery-order-row--clickable` : completionClass;
    };
}

return { __sfc: true,props, emit, locale, expandedOrderIds, tableOrders, columns, rowSelection, emptyText, debouncedClientSearch, handleDateAdd, handleDateRemove, handleClientSearch, handleClientChange, handleDeliveryRegionChange, getInternalOrderColumns, toggleExpand, getCompletionRowClass, onProjectRowEvent }
}

})